import { SyncRechargeData } from '../types/recharge';
import type { CheckoutEventData } from '../types/checkout';

export const formatMsisdn = (msisdn: string): string => {
  if (!msisdn) return '';
  const digits = msisdn.replace(/\D/g, '');
  return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const formatDate = (date: string | number | Date): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR');
};

export const formatDateTime = (date: string | number | Date): string => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  return `${day} às ${d.getHours()}h${pad2(d.getMinutes())}`;
};

// Encurta uma string longa (ex: código Pix) pra caber numa linha só, cortando
// só o final com reticências. Feito no JS (não via CSS text-overflow) porque
// text-overflow:ellipsis não centraliza texto truncado — o texto visível fica
// sempre ancorado no início, mesmo com text-align:center.
export const truncateEnd = (text: string, maxChars = 36): string => {
  if (!text || text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}...`;
};

// Contador do Pix: sempre hh:mm:ss, mesmo abaixo de 1h, como no checkout.
export const formatCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
};

// "25/09/2026 às 16:20", no fuso do aparelho. Manual pelo mesmo motivo do
// formatCurrency: sem ICU pt-BR o toLocaleString muda o formato.
export const formatPixDeadline = (date: string | number | Date): string => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} às ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// Formatação manual (sem toLocaleString): ambientes sem ICU pt-BR (ex: node
// do runner alpine no CI) caem em fallback en-US e trocam a vírgula por ponto.
// Usa \u00A0 (espaço que não quebra linha) entre "R$" e o valor — mesmo
// caractere que o toLocaleString('pt-BR') original produzia, para não mudar
// o comportamento de quebra de linha nas telas que já usam essa função.
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const reais = Number(value) / 100;
  const isNegative = reais < 0;
  const [intPart, decPart] = Math.abs(reais).toFixed(2).split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${isNegative ? '-' : ''}R$\u00A0${withThousands},${decPart}`;
};

export const formatCurrencyRaw = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const formatCurrencyInteger = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return '';
  return String(Number(value) / 100);
};

const WEEKLY_DAYS = [
  { day: 'Domingo', every: 'Todo' },
  { day: 'Segunda-feira', every: 'Toda' },
  { day: 'Terça-feira', every: 'Toda' },
  { day: 'Quarta-feira', every: 'Toda' },
  { day: 'Quinta-feira', every: 'Toda' },
  { day: 'Sexta-feira', every: 'Toda' },
  { day: 'Sábado', every: 'Todo' },
];

export const formatDay = (frequency: string, day: number): string => {
  const freq = frequency.toUpperCase();
  if (day > 31) {
    const date = new Date(parseInt(String(day), 10));
    if (freq === 'MENSAL') return `${date.getUTCDate()}`.padStart(2, '0');
    return WEEKLY_DAYS[date.getUTCDay()].day;
  }
  if (freq === 'MENSAL') return `${day}`.padStart(2, '0');
  return WEEKLY_DAYS[day].day;
};

export const formatFrequencyMessage = (
  frequency: string,
  day: string | number,
): string => {
  const freq = frequency.toUpperCase();
  const formattedDay = formatDay(frequency, Number(day));
  if (freq === 'MENSAL') return `Mensal - Todo dia ${formattedDay}`;
  const weekly = WEEKLY_DAYS.find(item => item.day === formattedDay);
  return `Semanal - ${weekly.every} ${formattedDay}`;
};

// Formata um valor em centavos como R$ X (sem decimais se inteiro).
export const formatRechargeValue = (valueInCents: number): string => {
  const reais = valueInCents / 100;
  return Number.isInteger(reais) ? `R$ ${reais}` : `R$ ${reais.toFixed(2)}`;
};

/**
 * Normaliza o retorno do syncRecharge (checkout) para o formato esperado pela página de sucesso.
 *
 * Entrada (syncRecharge): { id, nsu, date, selectedRechargeValue, source: { type, params: { bin, last, brand } } }
 * Saída (rechargeResult): { nsu, date, value, recipient, card: { bandeira, ultimosDigitos } }
 */
export const normalizeCheckoutResult = (
  data: SyncRechargeData,
  recipient: string,
) => ({
  nsu: data.nsu,
  date: data.date,
  value: data.selectedRechargeValue,
  recipient,
  paymentType: data.source?.type || null,
  card: data.source?.params
    ? {
        bandeira: data.source.params.brand,
        ultimosDigitos: data.source.params.last,
      }
    : null,
});

/**
 * Reduz o meio de pagamento que o checkout informa às chaves de
 * `TRANSLATE_PAYMENT_METHOD`. O checkout manda grafias diferentes conforme o
 * evento (`credit_card` no clique, `CREDIT_CARD` no terminal), então a regra
 * olha o conteúdo, não a string exata.
 *
 * Wallets perdem só o `_` (`google_pay` → `googlepay`), nunca o prefixo: cortar
 * no último `_` fazia Google Pay e Apple Pay virarem os dois `pay`. O Click to
 * Pay é testado primeiro para um `c2p_credit` não cair em `credit`.
 */
export const normalizePaymentMethod = (method: string): string => {
  const raw = (method ?? '').toLowerCase();
  if (raw.startsWith('c2p') || raw.replace(/_/g, '').includes('clicktopay'))
    return 'c2p';
  if (raw.includes('credit')) return 'credit';
  if (raw.includes('debit')) return 'debit';
  return raw.replace(/_/g, '');
};

export const getCheckoutPaymentMethod = (
  eventData: Pick<CheckoutEventData, 'transactions' | 'payment_method'> | null,
): string | undefined =>
  eventData?.transactions?.[0]?.method ?? eventData?.payment_method;

// Mesma regra do `normalizePaymentMethod`, com `null` quando não há método. A
// recarga e a troca de número leem o tipo por aqui, e o tracking do clique em
// Pagar pelo `normalizePaymentMethod`: delegar mantém uma regra só, para a tela
// de sucesso e o clique nunca discordarem (TPC-3835).
export const toPaymentType = (method: string | undefined): string | null =>
  method ? normalizePaymentMethod(method) : null;

export const toSnakeCase = (text: string | null | undefined): string =>
  (text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

export const toKebabCase = (text: string | null | undefined): string =>
  (text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const formatCPF = (cpf: string): string => {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '');
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};
